import { useState, useEffect, useCallback } from 'react';
import { API, authFetch } from '@/App';

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (_error) {
      console.error('Error checking subscription:', _error);
    }
  };

  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return null;
    
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      return registration;
    } catch (_error) {
      console.error('Service Worker registration failed:', _error);
      return null;
    }
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      return false;
    }

    setLoading(true);
    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setLoading(false);
        return false;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        setLoading(false);
        return false;
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const keyResponse = await authFetch(`${API}/chat/vapid-public-key`);
      if (!keyResponse.ok) {
        throw new Error('Failed to get VAPID key');
      }
      const { publicKey } = await keyResponse.json();

      // Convert base64 to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      const response = await authFetch(`${API}/chat/push-subscribe`, {
        method: 'POST',
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });

      if (response.ok) {
        setIsSubscribed(true);
        return true;
      } else {
        throw new Error('Failed to save subscription');
      }
    } catch (_error) {
      console.error('Error subscribing to push:', _error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();
        
        // Notify server
        await authFetch(`${API}/chat/push-unsubscribe`, {
          method: 'POST',
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });
      }
      
      setIsSubscribed(false);
      return true;
    } catch (_error) {
      console.error('Error unsubscribing:', _error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const testNotification = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/chat/test-push`, { method: 'POST' });
      return response.ok;
    } catch (_error) {
      console.error('Error testing notification:', _error);
      return false;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    testNotification
  };
}
